import React, { useEffect, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Form,
    ListGroup,
    Row,
    Spinner,
} from 'react-bootstrap';
import positionService from '@/shared/services/positionService';
import { candidacyService } from '@/shared/services/candidacyService';
import { HELD_CANDIDACY_STAGES } from '@/shared/utils/candidacyStage';
import { positionKeyFacts } from '@/shared/utils/positionKeyFacts';
import { positionCourse } from '@/shared/utils/positionText';
import { MAX_POSITIONS, POSITIONS_ID } from './studentApplicationForm';
import '@/features/positions/OpenPositionsPage.css';

const label = (p) =>
    `${p.subject} ${p.courseNumber}${p.section ? `, section ${p.section}` : ''}`;

/**
 * The positions step: the open positions to choose from, and the student's
 * ranked choices. `chosen` is [{ applicationId, summary? }] in rank order and
 * `setChosen` takes an updater. A chosen position that is no longer open is
 * shown as such with a remove control, unless the student holds it: the
 * server keeps a held position valid after it fills. One with an outstanding
 * offer is answered from the offers page and one accepted is the student's,
 * so neither has a remove control.
 */
function PositionChoices({
    program,
    term,
    chosen,
    setChosen,
    article,
    roleName,
    invalid,
    onRows,
}) {
    const [rows, setRows] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const [candidacies, setCandidacies] = useState([]);
    const [subject, setSubject] = useState('');
    const [query, setQuery] = useState('');

    useEffect(() => {
        let cancelled = false;
        setRows(null);
        setLoadError(false);
        positionService
            .listPositions({ program, term })
            .then((data) => {
                if (cancelled) return;
                setRows(data.positions || []);
                onRows?.(data.positions || []);
            })
            .catch(() => !cancelled && setLoadError(true));
        return () => {
            cancelled = true;
        };
        // onRows only stores what it is given
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [program, term, attempt]);

    useEffect(() => {
        let cancelled = false;
        candidacyService
            .getMine()
            .then((d) => !cancelled && setCandidacies(d.candidacies || []))
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, []);

    const add = (row) =>
        setChosen((prev) =>
            prev.length >= MAX_POSITIONS ||
            prev.some((p) => p.applicationId === row.applicationId)
                ? prev
                : [...prev, { applicationId: row.applicationId, summary: row }]
        );

    const move = (index, direction) =>
        setChosen((prev) => {
            const swap = index + direction;
            if (swap < 0 || swap >= prev.length) return prev;
            const next = [...prev];
            [next[index], next[swap]] = [next[swap], next[index]];
            return next;
        });
    const remove = (id) =>
        setChosen((prev) => prev.filter((p) => p.applicationId !== id));

    const byId = new Map((rows || []).map((r) => [r.applicationId, r]));
    const stageOf = new Map(
        candidacies
            .filter((c) => c.program === program && c.term === term)
            .map((c) => [c.applicationId, c.stage])
    );
    const summaryOf = (p) =>
        byId.get(p.applicationId) ||
        p.summary ||
        candidacies.find((c) => c.applicationId === p.applicationId)?.position;
    const chosenIds = new Set(chosen.map((p) => p.applicationId));
    const unchosen = (rows || []).filter(
        (r) => !chosenIds.has(r.applicationId)
    );
    const subjects = [
        ...new Set(unchosen.map((r) => r.subject).filter(Boolean)),
    ].sort();
    const text = query.trim().toLowerCase();
    const available = unchosen.filter(
        (r) =>
            (!subject || r.subject === subject) &&
            (!text ||
                [
                    positionCourse(r),
                    positionCourse(r).replace(/\s+/g, ''),
                    r.title,
                    r.instructorName,
                    r.recruitingMessage,
                ].some((h) => h && h.toLowerCase().includes(text)))
    );
    const full = chosen.length >= MAX_POSITIONS;

    return (
        <div>
            <h2 className="h5 mb-3" id={POSITIONS_ID} tabIndex={-1}>
                Open Positions
            </h2>
            <p className="text-muted mb-3">
                Choose the sections you would like to support as {article}{' '}
                {roleName}, then rank them. Your top choice should be rank 1.
            </p>

            {invalid && (
                <p className="text-danger small" role="alert">
                    Choose at least one position, or answer Yes to the general
                    pool question below.
                </p>
            )}

            {chosen.length > 0 && (
                <Card className="bg-light border-0 mb-4">
                    <Card.Body>
                        <h3 className="h6">Your ranked choices</h3>
                        <ol className="list-unstyled mb-0">
                            {chosen.map((p, index) => {
                                const row = summaryOf(p);
                                const open = rows && byId.has(p.applicationId);
                                const stage = stageOf.get(p.applicationId);
                                const held =
                                    HELD_CANDIDACY_STAGES.includes(stage);
                                const answered = stage === 'offered';
                                const accepted = stage === 'accepted';
                                const fixed = answered || accepted;
                                return (
                                    <li
                                        key={p.applicationId}
                                        className="d-flex flex-wrap align-items-center justify-content-between gap-2 py-2"
                                    >
                                        <span>
                                            <Badge
                                                bg="secondary"
                                                pill
                                                className="me-2"
                                            >
                                                {index + 1}
                                            </Badge>
                                            <strong>
                                                {row
                                                    ? label(row)
                                                    : 'A position you chose'}
                                            </strong>
                                            {row?.title && (
                                                <span className="text-muted ms-2">
                                                    {row.title}
                                                </span>
                                            )}
                                            {rows && !open && !held && (
                                                <Badge
                                                    bg="warning"
                                                    text="dark"
                                                    className="ms-2"
                                                >
                                                    No longer open
                                                </Badge>
                                            )}
                                            {row && (
                                                <span className="d-block small text-muted">
                                                    {positionKeyFacts(row)}
                                                </span>
                                            )}
                                            {answered && (
                                                <span className="d-block small text-muted">
                                                    You have an offer for this
                                                    position. Answer it from
                                                    your offers.
                                                </span>
                                            )}
                                            {accepted && (
                                                <span className="d-block small text-muted">
                                                    You accepted this position.
                                                </span>
                                            )}
                                        </span>
                                        <span>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="me-1 psh-move-btn"
                                                aria-label={`Move ${row ? label(row) : 'position'} up`}
                                                disabled={index === 0}
                                                onClick={() => move(index, -1)}
                                            >
                                                &#9650;
                                            </Button>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                className="me-1 psh-move-btn"
                                                aria-label={`Move ${row ? label(row) : 'position'} down`}
                                                disabled={
                                                    index === chosen.length - 1
                                                }
                                                onClick={() => move(index, 1)}
                                            >
                                                &#9660;
                                            </Button>
                                            {fixed ? (
                                                // keeps the arrows in line with
                                                // the rows that have Remove
                                                <span
                                                    className="btn btn-sm invisible"
                                                    aria-hidden="true"
                                                >
                                                    Remove
                                                </span>
                                            ) : (
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    aria-label={`Remove ${row ? label(row) : 'position'}`}
                                                    onClick={() =>
                                                        remove(p.applicationId)
                                                    }
                                                >
                                                    Remove
                                                </Button>
                                            )}
                                        </span>
                                    </li>
                                );
                            })}
                        </ol>
                    </Card.Body>
                </Card>
            )}

            {rows === null && !loadError && (
                <div className="text-center py-4">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2 text-muted">
                        Loading positions...
                    </span>
                </div>
            )}
            {loadError && (
                <Alert variant="danger" className="text-center">
                    <p className="mb-2">
                        Couldn&apos;t load the open positions. Please try again.
                    </p>
                    <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => setAttempt((n) => n + 1)}
                    >
                        Retry
                    </Button>
                </Alert>
            )}
            {rows && unchosen.length > 0 && (
                <Form
                    role="search"
                    aria-label="Filter positions"
                    className="mb-3"
                    onSubmit={(e) => e.preventDefault()}
                >
                    <Row className="g-2">
                        <Col sm={4}>
                            <Form.Group controlId="sap-positions-subject">
                                <Form.Label>Subject</Form.Label>
                                <Form.Select
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                >
                                    <option value="">All subjects</option>
                                    {subjects.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col sm={8}>
                            <Form.Group controlId="sap-positions-search">
                                <Form.Label>Search positions</Form.Label>
                                <Form.Control
                                    type="search"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Form>
            )}
            {rows && unchosen.length === 0 && (
                <p className="text-muted">
                    {rows.length === 0
                        ? 'No positions are open right now.'
                        : 'You have chosen every open position.'}
                </p>
            )}
            {rows && unchosen.length > 0 && available.length === 0 && (
                <p className="text-muted" role="status">
                    No positions match these filters.{' '}
                    <Button
                        variant="link"
                        size="sm"
                        className="p-0 align-baseline"
                        onClick={() => {
                            setSubject('');
                            setQuery('');
                        }}
                    >
                        Clear filters
                    </Button>
                </p>
            )}
            {rows && unchosen.length > 0 && full && (
                <p className="text-muted" role="status">
                    You can choose up to {MAX_POSITIONS} positions. Remove one
                    to choose another.
                </p>
            )}
            {rows && available.length > 0 && (
                <ListGroup className="mb-3 position-choice-list">
                    {available.map((row) => (
                        <ListGroup.Item
                            key={row.applicationId}
                            className="d-flex flex-wrap justify-content-between align-items-start gap-2"
                        >
                            <div>
                                <strong>{label(row)}</strong>
                                <span className="text-muted ms-2">
                                    {row.title}
                                </span>
                                {row.instructorName && (
                                    <small className="text-muted d-block">
                                        {row.instructorName}
                                    </small>
                                )}
                                {row.recruitingMessage && (
                                    <small className="d-block psh-clamp-2 psh-recruiting-message">
                                        {row.recruitingMessage}
                                    </small>
                                )}
                                <small className="d-block">
                                    {positionKeyFacts(row)}
                                </small>
                            </div>
                            <Button
                                variant="outline-primary"
                                size="sm"
                                aria-label={`Add ${label(row)}`}
                                disabled={full}
                                onClick={() => add(row)}
                            >
                                + Add
                            </Button>
                        </ListGroup.Item>
                    ))}
                </ListGroup>
            )}
        </div>
    );
}

export default PositionChoices;
